import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { ResendSigninCodeApi, VerifySigninApi } from "../../API/auth";
import type { SigninChallenge, SigninSession } from "../../API/auth";
import useFetch from "../../hooks/useFetch";
import { EnterpriseAuthContainer } from "./component/EnterpriseAuthContainer";
import { EnterpriseAuthImagePanel } from "./component/ImagePanel";
import Button from "./component/EnterpriseButton";
import {
  emptySigninVerificationValues,
  signinVerificationSchema,
} from "./validation";
import { handleSigninVerification } from "./HandleSigninNavigation";
import VerificationCodeInput from "./component/VerificationCodeInput";

interface SigninVerificationState {
  challengeId: string;
  redirect: string | null;
}

export const SigninVerificationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const verification = location.state as SigninVerificationState | null;

  const challengeId = verification?.challengeId || "";
  const [feedback, setFeedback] = useState("");

  const { load: verifyCode, isLoading: isVerifying } = useFetch<SigninSession>({
    showToast: false,
  });

  const { load: resendCode, isLoading: isResending } =
    useFetch<SigninChallenge>({ showToast: false });

  const formik = useFormik({
    initialValues: emptySigninVerificationValues,
    validationSchema: signinVerificationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      if (!challengeId || isVerifying || isResending) return;

      setFeedback("");

      try {
        const response = await verifyCode(
          VerifySigninApi({
            challengeId,
            trustThisDevice: values.trustDevice,
            code: values.code,
          }),
        );

        handleSigninVerification(response.data, verification?.redirect);
      } catch (error) {
        setFeedback(
          error instanceof Error
            ? error.message
            : "Unable to verify the code. Please try again.",
        );
      }
    },
  });

  const isBusy = formik.isSubmitting || isVerifying || isResending;

  const codeError = formik.touched.code ? formik.errors.code : undefined;

  const handleResend = async () => {
    if (!challengeId || isBusy) return;

    setFeedback("");

    try {
      const response = await resendCode(ResendSigninCodeApi(challengeId));

      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        {
          replace: true,
          state: {
            ...verification,
            challengeId: response.data.challengeId,
          },
        },
      );

      formik.resetForm({
        values: {
          code: "",
          trustDevice: formik.values.trustDevice,
        },
      });

      setFeedback(response.message);
    } catch (error) {
      setFeedback(
        error instanceof Error
          ? error.message
          : "Unable to resend the code. Please try again.",
      );
    }
  };

  if (!challengeId) {
    return <Navigate to="/enterprise/signin" replace />;
  }

  return (
    <main className="flex min-h-screen flex-col bg-BASE_BG md:flex-row">
      <EnterpriseAuthContainer>
        <section className="w-full font-SatoshiR">
          <h1
            id="signin-verification-title"
            className="font-SatoshiB text-3xl leading-10 tracking-tight text-MAIN_TEXT"
          >
            Verify it’s you
          </h1>

          <p className="mt-2 text-base leading-6 text-bash">
            We sent a verification code to your email. Enter the code from the
            email in the field below.
          </p>

          <form noValidate onSubmit={formik.handleSubmit} className="mt-7">
            <VerificationCodeInput
              value={formik.values.code}
              onChange={(code) => {
                setFeedback("");
                void formik.setFieldValue("code", code);
              }}
              onBlur={() => {
                void formik.setFieldTouched("code", true);
              }}
              disabled={isBusy}
              error={codeError}
            />
            <label
              htmlFor="trust-signin-device"
              className="mt-6 flex cursor-pointer items-start gap-2 font-SatoshiM text-sm leading-5 text-MAIN_TEXT"
            >
              <input
                id="trust-signin-device"
                name="trustDevice"
                type="checkbox"
                checked={formik.values.trustDevice}
                onChange={formik.handleChange}
                disabled={isBusy}
                className="mt-0.5 h-4 w-4 shrink-0 rounded accent-bblue disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bblue"
              />

              <span>Trust this device for 60 days?</span>
            </label>

            <Button
              text="Verify Code"
              type="primary"
              disabled={!formik.isValid || isBusy}
              isLoading={formik.isSubmitting || isVerifying}
              className="mt-6 w-full"
            />
          </form>

          <p className="mt-3 text-base leading-6 text-bash">
            Didn’t get the code?{" "}
            <button
              type="button"
              onClick={handleResend}
              disabled={isBusy}
              className="font-SatoshiM text-bblue underline underline-offset-2 enabled:hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bblue"
            >
              {isResending ? "Sending..." : "Resend the code"}
            </button>
          </p>

          {feedback && (
            <p role="status" className="mt-3 text-sm leading-5 text-bash">
              {feedback}
            </p>
          )}
        </section>
      </EnterpriseAuthContainer>

      <EnterpriseAuthImagePanel />
    </main>
  );
};
